import {Checkbox, DataStatus, FormControl, Separator, Table, TableRow} from 'platform/components';
import {Box, HStack, Text} from 'platform/foundation';
import {useCurrencySymbolFormatter, useFormatCurrency, useFormatNumber} from 'platform/locale';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetRentalPriceGroupQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {EMPTY_PLACEHOLDER, useTenant} from '@dms/shared';

import {getPriceListColumns} from '../utils/getPriceListColumns';

export type FormValues = {
  priceGroupItemId: string | null;
  priceGroupId: string | null;
};

interface PriceGroupTableProps {
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
}

export function PriceGroupTable(props: PriceGroupTableProps) {
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();
  const formatCurrencySymbol = useCurrencySymbolFormatter();
  const {tenantCurrency} = useTenant();

  const selectedPriceGroupId = useWatch({control: props.control, name: 'priceGroupId'});
  const selectedPriceGroupItemId = useWatch({control: props.control, name: 'priceGroupItemId'});

  const {
    data: priceGroupDetail,
    isLoading: isLoadingPriceGroupDetail,
    isError: isPriceGroupDetailError,
    isFetching: isFetchingPriceGroupDetail,
  } = useGetRentalPriceGroupQuery(
    {rentalPriceGroupId: selectedPriceGroupId ?? ''},
    {skip: !selectedPriceGroupId}
  );

  const handleCheckRow = (priceGroupItemId: string) => {
    props.formApi.clearErrors('priceGroupItemId');
    props.formApi.setValue('priceGroupItemId', priceGroupItemId);
  };

  return (
    <DataStatus
      spacing={10}
      isEmpty={isNilOrEmpty(selectedPriceGroupId) || isNilOrEmpty(priceGroupDetail?.items)}
      isLoading={isLoadingPriceGroupDetail || isFetchingPriceGroupDetail}
      isError={isPriceGroupDetailError}
      emptyMessage={
        isNilOrEmpty(priceGroupDetail?.items) && isNotNilOrEmpty(selectedPriceGroupId)
          ? i18n.t('entity.rental.labels.priceGroupWithoutItems')
          : i18n.t('entity.rental.labels.notAssignedPriceGroup')
      }
    >
      <Separator spacing={0} />

      <Table columns={getPriceListColumns(formatCurrencySymbol(tenantCurrency))} variant="bordered">
        {priceGroupDetail?.items.map((item) => (
          <TableRow
            key={item.rentalPriceGroupItemId}
            actionsWidth="5%"
            onRowClick={() => handleCheckRow(item.rentalPriceGroupItemId)}
          >
            <Box padding={2}>
              <HStack spacing={4} align="center">
                <Checkbox
                  value={selectedPriceGroupItemId === item.rentalPriceGroupItemId}
                  errorMessage={
                    isNotNilOrEmpty(props.formApi.formState.errors.priceGroupItemId)
                      ? i18n.t('general.validations.fieldIsRequired')
                      : undefined
                  }
                  label=""
                />
                <Text alternative>{item.name || EMPTY_PLACEHOLDER}</Text>
              </HStack>
            </Box>
            <Box padding={2}>
              <Text>{formatNumber(item.range.from)}</Text>
            </Box>
            <Box padding={2}>
              <Text>{formatNumber(item.range.to)}</Text>
            </Box>
            <Box padding={2}>
              <Text>{i18n.t(`entity.rental.labels.${item.range.period}`)}</Text>
            </Box>
            <Box padding={2}>
              <Text>{formatCurrency(parseFloat(item.price.rate), tenantCurrency)}</Text>
            </Box>
            <Box padding={2}>
              <Text>
                {formatCurrency(parseFloat(item.price.ratePerKilometer), tenantCurrency, 2)}
              </Text>
            </Box>
            <Box padding={2}>
              <Text>{formatNumber(item.price.includedKilometers)} Km</Text>
            </Box>
          </TableRow>
        ))}
      </Table>
    </DataStatus>
  );
}
